import { useCallback } from 'react';

import toast from 'react-hot-toast';
import { Node, getNodesBounds, useReactFlow, useStoreApi } from 'reactflow';

import useDetachNodes from './useDetachNodes';
import { FLOW_TYPE } from 'constants/flow';
import { getNodeId } from '../utils/nodeUtils';

const padding = 10;

function useGroup<NodeData, EdgeData>() {
  const { getNodes, getEdges, getNode, setNodes, setEdges } = useReactFlow<NodeData, EdgeData>();
  const store = useStoreApi();
  const detachNodes = useDetachNodes();

  const group = useCallback(
    (selectedNodes: Node[], callback?: () => void) => {
      if (selectedNodes.filter((node) => node.type === FLOW_TYPE.NODE.DATA_NODE).length > 0) {
        return toast.error('데이터 블록은 그룹을 만들 수 없어요');
      }
      const id = getNodeId('group');
      const nodes = getNodes();
      const selectedNodeIds = selectedNodes.map((node) => node.id);
      const rectOfNodes = getNodesBounds(selectedNodes);
      const parentPosition = {
        x: rectOfNodes.x,
        y: rectOfNodes.y,
      };

      // 선택된 노드들 중 외부와 연결된 노드 확인
      const sourceNodeIds = [] as string[];
      const targetNodeIds = [] as string[];

      getEdges().forEach((edge) => {
        const source = edge.sourceHandle!.replace('_source', '');
        const target = edge.targetHandle!.replace('_target', '');

        const hasSource = selectedNodeIds.includes(source);
        const hasTarget = selectedNodeIds.includes(target);

        if (hasSource && !hasTarget) {
          sourceNodeIds.push(source);
        }
        if (!hasSource && hasTarget) {
          targetNodeIds.push(target);
        }
      });

      const groupNode = {
        id,
        type: FLOW_TYPE.NODE.GROUP_NODE,
        position: parentPosition,
        zIndex: -1,
        data: {
          name: `group_${new Date().getTime()}`,
          expand: true,
          sourceNodeIds,
          targetNodeIds,
        },
        style: {
          width: rectOfNodes.width + padding * 2,
          height: rectOfNodes.height + padding * 2,
        },
      } as Node;
      const nextNodes: Node[] = nodes.map((node) => {
        if (selectedNodeIds.includes(node.id)) {
          return {
            ...node,
            position: {
              x: node.position.x - parentPosition.x + padding,
              y: node.position.y - parentPosition.y + padding,
            },
            extent: 'parent',
            parentNode: id,
            // selected: false,
            data: {
              ...node.data,
              source: sourceNodeIds.includes(node.id) as boolean,
              target: targetNodeIds.includes(node.id) as boolean,
            },
          };
        }
        return node;
      });

      if (callback) {
        callback();
      }

      setNodes([groupNode, ...nextNodes]);
      store.getState().resetSelectedElements();
      store.setState({ nodesSelectionActive: false });
    },
    [store, getNodes, getEdges, setNodes]
  );

  const unGroup = useCallback(
    (id: string) => {
      const childNodeIds = Array.from(store.getState().nodeInternals.values())
        .filter((n) => n.parentNode === id)
        .map((n) => n.id);

      detachNodes(childNodeIds, id);
      setEdges((edges) =>
        edges.map((edge) => {
          const newEdge = { ...edge };

          if (edge.source === id) {
            newEdge.source = edge.sourceHandle!.replace('_source', '');
          }
          if (edge.target === id) {
            newEdge.target = edge.targetHandle!.replace('_target', '');
          }

          if (childNodeIds.includes(newEdge.source)) {
            newEdge.hidden = false;
          }
          if (childNodeIds.includes(newEdge.target)) {
            newEdge.hidden = false;
          }

          return newEdge;
        })
      );
    },
    [store, detachNodes, setEdges]
  );

  const toggleSourceTargetNode = useCallback(
    (id: string, type: 'source' | 'target') => {
      const node = getNode(id);
      const parentNodeId = node?.parentNode;
      const parentNode = parentNodeId ? getNode(parentNodeId) : undefined;

      if (!parentNode) return;

      const ids = (parentNode.data as any)[`${type}NodeIds`] as string[];
      const isSourceTarget = ids.includes(id);

      if (isSourceTarget) {
        (parentNode.data as any)[`${type}NodeIds`] = ids.filter((cntId) => cntId !== id);
      } else {
        (parentNode.data as any)[`${type}NodeIds`] = [...ids, id];
      }

      setNodes((prev) =>
        prev.map((cntNode) => {
          if (cntNode.id === parentNodeId) {
            return parentNode;
          }
          if (cntNode.id === id) {
            return {
              ...cntNode,
              data: {
                ...cntNode.data,
                [type]: !isSourceTarget,
              },
            };
          }
          return cntNode;
        })
      );
    },
    [setNodes, getNode]
  );

  const checkSourceTargetNode = useCallback(
    (id: string) => {
      const node = getNode(id);
      const parentNodeId = node?.parentNode;
      const parentNode = parentNodeId ? getNode(parentNodeId) : undefined;

      if (!parentNode) return undefined;

      const sourceNodeIds = (parentNode.data as any).sourceNodeIds as string[];
      const targetNodeIds = (parentNode.data as any).targetNodeIds as string[];

      return {
        isSource: sourceNodeIds.includes(id),
        isTarget: targetNodeIds.includes(id),
      };
    },
    [getNode]
  );

  return {
    group,
    unGroup,
    checkSourceTargetNode,
    toggleSourceTargetNode,
  };
}

export default useGroup;
